import React, { useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../apiServices/axios';
import { Eye16Regular, EyeOff16Regular } from '@fluentui/react-icons';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logoutWithAudit } from '../../redux/usersSlice';
import styles from './ChangePasswordModal/css/ChangePasswordModal.module.css';

const SSOUnlinkModal = ({ isOpen, onClose }) => {
    const [newPassword, setNewPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const handleSubmit = async e => {
        e.preventDefault();

        // Validation
        if (!newPassword) {
            toast.error('New password is required');
            return;
        }

        if (newPassword.length < 8) {
            toast.error('New password must be at least 8 characters long');
            return;
        }

        setIsLoading(true);
        try {
            const response = await axiosInstance.post('accounts/settings/unlink-azure/', {
                new_password: newPassword,
            });

            toast.success('Password changed successfully');
            setIsSuccess(true);
        } catch (error) {
            console.error('Error unlinking SSO:', error);

            // Handle different error response formats
            let errorMessage = 'Failed to unlink SSO account';

            if (error.response?.data) {
                const errorData = error.response.data;

                // Handle array format errors (e.g., {"new_password": ["The password is too similar to the email."]})
                if (typeof errorData === 'object') {
                    const errorKeys = Object.keys(errorData);
                    if (errorKeys.length > 0) {
                        const firstKey = errorKeys[0];
                        const firstError = errorData[firstKey];

                        if (Array.isArray(firstError) && firstError.length > 0) {
                            errorMessage = firstError[0];
                        } else if (typeof firstError === 'string') {
                            errorMessage = firstError;
                        }
                    }
                }
                // Handle string format errors
                else if (typeof errorData === 'string') {
                    errorMessage = errorData;
                }
                // Handle nested error formats
                else if (errorData.error) {
                    errorMessage = errorData.error;
                } else if (errorData.detail) {
                    errorMessage = errorData.detail;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLoginClick = async () => {
        // Clear localStorage and cookies
        localStorage.removeItem('persist:root');

        // Dispatch logout action (records workspace audit, then clears session)
        await dispatch(logoutWithAudit());

        // Navigate to login page
        navigate('/login');

        // Close modal
        handleClose();
    };

    const handleClose = () => {
        setNewPassword('');
        setShowNewPassword(false);
        setIsLoading(false);
        setIsSuccess(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Modal show={isOpen} onHide={handleClose} centered>
            <Modal.Body className={styles.modalBody}>
                {!isSuccess ? (
                    <>
                        <h6 className={styles.modalTitle}>Set New Password</h6>
                        <p className={styles.modalDescription}>Your new password must be different from previously used passwords.</p>

                        <form onSubmit={handleSubmit} className={styles.passwordForm}>
                            <div>
                                <p className={styles.fieldLabel}>Enter new password</p>
                                <div className="input-field">
                                    <input
                                        value={newPassword}
                                        name="password"
                                        type={showNewPassword ? 'text' : 'password'}
                                        onChange={e => setNewPassword(e.target.value)}
                                        className={'form-control ' + styles.inputField}
                                        required
                                        minLength="8"
                                    />
                                    {showNewPassword ? (
                                        <Eye16Regular onClick={() => setShowNewPassword(!showNewPassword)} className="icon icon-right svg-icons" />
                                    ) : (
                                        <EyeOff16Regular onClick={() => setShowNewPassword(!showNewPassword)} className="icon icon-right svg-icons" />
                                    )}
                                </div>
                            </div>

                            <div className=" mt-3">
                                <button type="submit" className={styles.saveButton} disabled={isLoading}>
                                    {isLoading ? (
                                        <>
                                            <span className={styles.spinner}></span>
                                            Saving...
                                        </>
                                    ) : (
                                        'Save'
                                    )}
                                </button>
                            </div>
                        </form>
                    </>
                ) : (
                    <>
                        <h6 className={styles.modalTitle}>Password Reset</h6>
                        <p className={styles.modalDescription}>Your password has been successfully reset. Click below to continue your access.</p>

                        <div className=" mt-3">
                            <button onClick={handleLoginClick} className={styles.saveButton}>
                                Login
                            </button>
                        </div>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default SSOUnlinkModal;
